/**
 * Kurz-Phantom: ein Wort aus einer fast leeren Aufnahme. (Ticket 2443, 21.09.2026)
 *
 * ## Der Fall
 *
 * Martin drückt versehentlich kurz auf Aufnahme, sagt nichts. Der Upload hat
 * **3.424 Bytes** (rund eine Sekunde Opus). Whisper macht daraus „Ewa," — vier
 * Zeichen. Der Stille-Wächter (`wirktStill`) lässt es durch, die Sperrliste
 * kennt „Ewa" nicht, und ein ganzer Antwortzug läuft: *„Ewa verstehe ich
 * nicht."* Martin: *„Ich dachte wir hatten diese verdammten Halluzinationen
 * jetzt endlich im Griff."*
 *
 * ## Warum keine Sperrliste
 *
 * Das erfundene Wort heisst jedes Mal anders — „Ewa", „Danke", „Untertitel".
 * Eine Liste, die Wörter verbietet, läuft dem Modell immer hinterher. Die
 * Antwort ist die Erlaubnisliste (18.09.: „Sperrliste englisch, Antwort
 * deutsch"): Was aus einer sehr kurzen Aufnahme kommen DARF, ist eine kurze
 * Antwort — ja, nein, weiter, stopp. Alles andere aus einer Aufnahme, die
 * für einen Satz gar nicht lang genug war, ist ein Phantom.
 *
 * ## Die Zahlen (14 Tage, 309 Sprachnachrichten von Martin)
 *
 *     echte Nachrichten:   kleinste 39.316 Bytes (11 Wörter), Median 257 KB
 *     Bytes je Sekunde:    rund 3.545 (Opus/WebM)
 *     „Ewa,":              3.424 Bytes  ≈ 1 s
 *
 * Die Grenze von 8.000 Bytes (≈ 2,3 s) liegt fünfmal unter der kleinsten
 * echten Nachricht. Eine echte kurze Antwort („ja") liegt darunter — deshalb
 * die Erlaubnisliste, nicht die Grenze allein.
 *
 * ⚠️ Die Dateien tragen keine Dauer (308 von 309 ohne Metadaten); Bytes sind
 *    das einzige verlässliche Mass am Server.
 */

/** Unter so vielen Bytes ist keine Aufnahme lang genug für einen Satz. */
export const PHANTOM_MAX_BYTES = 8_000;

/** Höchstens so viele Wörter kann eine so kurze Aufnahme ehrlich tragen. */
const PHANTOM_MAX_WOERTER = 2;

/**
 * Was aus einer sehr kurzen Aufnahme kommen darf.
 *
 * ⚠️ Bewusst kurz und bewusst Erlaubnis statt Verbot. Wer hier etwas
 *    ergänzt, ergänzt eine Antwort, die ein Mensch in einer Sekunde gibt.
 */
export const KURZANTWORTEN: ReadonlySet<string> = new Set([
  "ja", "jа", "nein", "ok", "okay", "gut", "genau", "richtig", "stimmt",
  "stopp", "stop", "halt", "weiter", "los", "danke", "bitte", "hallo", "hi",
  "warte", "moment", "fertig", "passt", "super", "prima", "später", "morgen",
  "abbrechen", "abbruch", "wiederholen", "nochmal", "lauter", "leiser",
]);

function woerter(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Ist das ein Phantom aus einer fast leeren Aufnahme?
 *
 * Drei Bedingungen, alle drei zugleich: die Aufnahme ist zu kurz für einen
 * Satz, das Erkannte ist höchstens zwei Wörter, und keines davon ist eine
 * bekannte Kurzantwort.
 */
export function istKurzesPhantom(e: { bytes: number; transcript: string }): boolean {
  if (e.bytes >= PHANTOM_MAX_BYTES) return false;
  const w = woerter(e.transcript);
  if (w.length === 0) return true;
  if (w.length > PHANTOM_MAX_WOERTER) return false;
  return !w.some((x) => KURZANTWORTEN.has(x));
}
